/**
 * Индикатор объясняет себя сам.
 *
 * Правило для всех индикаторов платформы: линия отвечает «сколько»,
 * а человек без опыта спрашивает «и что». Поэтому у каждого есть табло
 * одного устройства:
 *
 *   - «Сейчас» — пилюля с именем состояния, цветом стороны;
 *   - «Что происходит» — одна-две фразы простыми словами;
 *   - ниже — две-три цифры, на которых вывод стоит.
 *
 * Одно устройство на всех — не ради красоты: глаз учится находить
 * вывод в одном и том же месте, и шестой индикатор читается так же
 * быстро, как первый. Табло выключается в настройках любого индикатора
 * одной и той же галочкой.
 */

import type { Candle } from '@specter/types';
import type { IndicatorShape, ReadoutRow } from './overlays.js';
import type { IndicatorParamSpec, IndicatorPoint } from './types.js';

export type Tone = 'bull' | 'bear' | 'warn' | 'muted';

/** Галочка табло — одна на все индикаторы: и ключ, и слова. */
export const TABLE_PARAM: IndicatorParamSpec = {
  key: 'table',
  label: 'Табло',
  hint: 'Сводка словами в углу графика: что сейчас происходит и что это значит. Выключите, если мешает.',
  type: 'boolean',
  default: true,
};

export interface Situation {
  readonly title: string;
  readonly tone: Tone;
  readonly text: string;
}

/** Первые две строки любого табло: состояние и что оно значит. */
export function situationRows(situation: Situation): ReadoutRow[] {
  return [
    { label: 'Сейчас', value: '', badge: { text: situation.title, tone: situation.tone } },
    { label: 'Что происходит', value: situation.text, full: true },
  ];
}

/** Цвет строки по тону состояния. */
export function rowTone(tone: Tone): 'up' | 'down' | 'muted' | undefined {
  return tone === 'bull' ? 'up' : tone === 'bear' ? 'down' : tone === 'muted' ? 'muted' : undefined;
}

/** Значения ряда по порядку свечей: пусто — `null`. */
export function valuesOf(points: readonly IndicatorPoint[] | undefined): (number | null)[] {
  return (points ?? []).map((point) => point.value);
}

/**
 * Последняя закрытая свеча.
 *
 * Выводы делаются по ней, а не по формирующейся: у той максимум и
 * закрытие ещё поменяются, и вывод прыгал бы на каждом тике.
 */
export function closedIndex(candles: readonly Candle[], livePartial: boolean): number {
  return livePartial ? candles.length - 2 : candles.length - 1;
}

/** Цена так, как её пишут: «80 768,5», «0,4521». */
export function priceWords(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

/** Процент со знаком: «+1,2%». */
export function percentWords(value: number, digits = 1): string {
  const text = Math.abs(value).toFixed(digits).replace('.', ',');
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${text}%`;
}

/** Во сколько раз: «×1,6». */
export function timesWords(value: number): string {
  return `×${value.toFixed(1).replace('.', ',')}`;
}

/**
 * Время свечи коротко: «18.09, 14:00 UTC».
 *
 * По UTC, как шкала графика: свечу из табло человек ищет глазами на
 * шкале, и «21:45» по местному времени там не найти — отметка стоит на
 * «18:45».
 */
export function whenWords(time: number): string {
  const text = new Date(time).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  return `${text} UTC`;
}

/** Наклон ряда за `bars` свечей до `end`: доля от значения. */
export function slopeOf(values: readonly (number | null)[], end: number, bars: number): number | null {
  const now = values[end];
  const before = values[end - bars];
  if (now === null || now === undefined || before === null || before === undefined || before === 0) return null;
  return (now - before) / Math.abs(before);
}

/** Среднее последних `bars` значений до `end` включительно. */
export function meanOf(values: readonly (number | null)[], end: number, bars: number): number | null {
  let sum = 0;
  let count = 0;
  for (let i = Math.max(0, end - bars + 1); i <= end; i += 1) {
    const value = values[i];
    if (value === null || value === undefined) continue;
    sum += value;
    count += 1;
  }
  return count === 0 ? null : sum / count;
}

export interface Cross {
  readonly index: number;
  readonly up: boolean;
}

/**
 * Пересечения двух рядов по закрытым свечам.
 *
 * `gap` — сколько свечей не ставить второе пересечение того же вида:
 * две линии, ползущие рядом, пересекаются на каждой свече, и частокол
 * отметок ничего бы не сказал.
 */
export function crossesOf(
  a: readonly (number | null)[],
  b: readonly (number | null)[],
  last: number,
  gap = 3,
): Cross[] {
  const out: Cross[] = [];
  let lastAt = -Infinity;
  for (let i = 1; i <= last; i += 1) {
    const a0 = a[i - 1];
    const b0 = b[i - 1];
    const a1 = a[i];
    const b1 = b[i];
    if (a0 == null || b0 == null || a1 == null || b1 == null) continue;
    const up = a0 <= b0 && a1 > b1;
    const down = a0 >= b0 && a1 < b1;
    if (!up && !down) continue;
    if (i - lastAt < gap) continue;
    out.push({ index: i, up });
    lastAt = i;
  }
  return out;
}

/** Отметка вывода на цене: над свечой для «вниз», под свечой для «вверх». */
export function markAt(candle: Candle, up: boolean, text: string): IndicatorShape {
  return { kind: 'mark', time: candle.time, price: up ? candle.low : candle.high, tone: up ? 'up' : 'down', text };
}
